export const OPS_PROFITABILITY_PRODUCTS = ['Cloud Management', 'SaaS Support', 'Cloud Professional Services', 'Resale Cloud'];
export const OPS_PROFITABILITY_FIELDS = ['presupuesto', 'presupuesto_con_actividad', 'costo_operativo', 'costo_administrativo', 'total_gastado'];
export const opsMoney = (value, currency) => value == null ? 'Pendiente' : `${new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(value)} ${currency}`;
export const opsPercent = value => value == null ? 'Pendiente' : `${Number(value).toLocaleString('es-MX', { maximumFractionDigits: 1 })}%`;
export const opsTone = value => value == null ? 'pending' : value > 0 ? 'bad' : 'ok';
export const opsDeviation = row => row.presupuesto == null || row.total_gastado == null ? null : row.total_gastado - row.presupuesto;
export const opsDeviationPercent = row => row.presupuesto > 0 && opsDeviation(row) != null ? opsDeviation(row) / row.presupuesto * 100 : null;
export const opsSortDeviations = rows => [...rows].sort((first, second) => second.saldo - first.saldo);
export const opsMonthEnd = month => `${month}-${String(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate()).padStart(2, '0')}`;
export const opsSum = values => values.some(value => value == null) ? null : Math.round(values.reduce((sum, value) => sum + Number(value), 0) * 100) / 100;

export function opsAggregate(rows) {
  const totals = Object.fromEntries(OPS_PROFITABILITY_FIELDS.map(field => [field, opsSum(rows.map(row => row[field]))]));
  totals.costo_parcial = rows.some(row => row.costo_parcial === true);
  totals.motivos_costo_parcial = [...new Set(rows.flatMap(row => row.motivos_costo_parcial || []))];
  totals.cobertura_completa = !totals.costo_parcial && rows.every(row => row.cobertura_completa !== false && row.total_gastado != null);
  totals.saldo = totals.presupuesto == null || totals.total_gastado == null ? null : Math.round((totals.total_gastado - totals.presupuesto) * 100) / 100;
  totals.porcentaje_gastado = totals.presupuesto > 0 && totals.total_gastado != null ? totals.total_gastado / totals.presupuesto * 100 : null;
  return totals;
}

export function opsDefaultProducts(roles, current) {
  const broad = roles.some(role => ['admin', 'finops_gerente', 'innovation_gerente'].includes(role));
  const managed = !broad && roles.some(role => ['kloud_ops_gerente', 'servicios_gerente'].includes(role));
  return OPS_PROFITABILITY_PRODUCTS.filter(product => product !== 'Resale Cloud' || (!current && !managed));
}

export function opsFilterClients(data, products, search, csm, overOnly) {
  return data.flatMap(customer => {
    const selected = customer.productos.filter(product => products.includes(product.producto));
    if (!selected.length) return [];
    const allWarnings = new Set(customer.productos.flatMap(product => product.warnings || []));
    const warnings = [...new Set([...(customer.warnings || []).filter(warning => !allWarnings.has(warning)), ...selected.flatMap(product => product.warnings || [])])];
    const row = { ...customer, ...opsAggregate(selected), warnings, productos: selected };
    const query = search.trim().toLocaleLowerCase('es-MX');
    const matches = `${row.nombre_comercial} ${row.csm || ''}`.toLocaleLowerCase('es-MX').includes(query);
    return matches && (!csm || row.csm === csm) && (!overOnly || (row.saldo != null && row.saldo > 0)) ? [row] : [];
  });
}

export function opsProductTeams(product) {
  const teams = new Map();
  (product?.oportunidades || []).forEach(opportunity => {
    (opportunity.equipos || []).forEach(team => {
      const key = String(team.team_id || team.nombre || 'SIN_EQUIPO').trim().toLowerCase();
      const grouped = teams.get(key) || { ...team, perfiles: [], oportunidades: new Map(), _profiles: new Map(),
        _opCost: [], _adminCost: [], _totalCost: [], horas_operativas: 0, horas_administrativas: 0 };
      const opKey = String(opportunity.oportunidad || '');
      const groupedOpportunity = grouped.oportunidades.get(opKey) || {
        oportunidad: opportunity.oportunidad, activa: opportunity.activa === true,
        horas_operativas: 0, horas_administrativas: 0, servicios: new Map(),
      };
      groupedOpportunity.activa ||= opportunity.activa === true;
      (opportunity.servicios || []).forEach(service => {
        const quoteKey = JSON.stringify([service.quote_line, service.sku_product, service.servicio, service.tipo_cargo]);
        if (!groupedOpportunity.servicios.has(quoteKey)) groupedOpportunity.servicios.set(quoteKey, { ...service });
      });
      ['costo_operativo', 'costo_administrativo', 'total_gastado'].forEach(field => {
        const target = field === 'costo_operativo' ? grouped._opCost : field === 'costo_administrativo' ? grouped._adminCost : grouped._totalCost;
        target.push(team[field]);
      });
      grouped.horas_operativas += Number(team.horas_operativas || 0);
      grouped.horas_administrativas += Number(team.horas_administrativas || 0);
      (team.perfiles || []).forEach(profile => {
        const profileKey = String(profile.nombre || 'Sin perfil').trim().toLowerCase();
        const groupedProfile = grouped._profiles.get(profileKey) || { ...profile, registros: [], _opCost: [], _adminCost: [], _totalCost: [],
          horas_operativas: 0, horas_administrativas: 0, puede_ver_detalle: false };
        ['costo_operativo', 'costo_administrativo', 'total_gastado'].forEach((field, index) => {
          groupedProfile[['_opCost', '_adminCost', '_totalCost'][index]].push(profile[field]);
        });
        if (team.puede_ver_detalle === true) {
          groupedProfile.puede_ver_detalle = true;
          (profile.registros || []).forEach(record => {
            const activity = { ...record, oportunidad: opportunity.oportunidad, oportunidad_activa: opportunity.activa === true };
            groupedProfile.registros.push(activity);
            if (activity.categoria === 'Operativo') groupedProfile.horas_operativas += Number(activity.horas || 0);
            else groupedProfile.horas_administrativas += Number(activity.horas || 0);
          });
        }
        grouped._profiles.set(profileKey, groupedProfile);
      });
      const authorizedHours = (team.perfiles || []).filter(() => team.puede_ver_detalle === true)
        .flatMap(profile => profile.registros || []);
      authorizedHours.forEach(record => {
        if (record.categoria === 'Operativo') groupedOpportunity.horas_operativas += Number(record.horas || 0);
        else groupedOpportunity.horas_administrativas += Number(record.horas || 0);
      });
      groupedOpportunity.servicios = [...groupedOpportunity.servicios.values()];
      grouped.oportunidades.set(opKey, groupedOpportunity);
      teams.set(key, grouped);
    });
  });
  const sum = values => values.some(value => value == null) ? null : values.reduce((total, value) => total + Number(value), 0);
  return [...teams.values()].map(team => ({ ...team,
    costo_operativo: sum(team._opCost), costo_administrativo: sum(team._adminCost), total_gastado: sum(team._totalCost),
    perfiles: [...team._profiles.values()].map(profile => ({ ...profile,
      costo_operativo: sum(profile._opCost), costo_administrativo: sum(profile._adminCost), total_gastado: sum(profile._totalCost),
    })),
    oportunidades: [...team.oportunidades.values()].map(opportunity => ({ ...opportunity,
      servicios: opportunity.servicios,
    })),
    _profiles: undefined, _opCost: undefined, _adminCost: undefined, _totalCost: undefined,
    puede_ver_detalle: (team.puede_ver_detalle === true) || [...team._profiles.values()].some(profile => profile.puede_ver_detalle),
  }));
}

export function opsWeekRanges(month) {
  const [year, monthNumber] = month.split('-').map(Number);
  const last = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const weekday = (new Date(Date.UTC(year, monthNumber - 1, 1)).getUTCDay() + 6) % 7;
  const result = [];
  let start = 1, end = Math.min(7 - weekday, last);
  while (start <= last) {
    result.push({ week: result.length + 1, start, end });
    start = end + 1;
    end = Math.min(start + 6, last);
  }
  return result;
}

export function opsTeamProfiles(clients) {
  const groups = new Map();
  clients.forEach(client => client.productos.forEach(product => product.oportunidades.forEach(op => op.equipos.forEach(team => team.perfiles.forEach(profile => {
    const key = JSON.stringify([team.team_id, profile.nombre]);
    const previous = groups.get(key) || { equipo: team.nombre, perfil: profile.nombre, costo_operativo: 0, costo_administrativo: 0, total_gastado: 0 };
    ['costo_operativo', 'costo_administrativo', 'total_gastado'].forEach(field => { previous[field] = opsSum([previous[field], profile[field]]); });
    groups.set(key, previous);
  })))));
  return [...groups.values()].sort((first, second) => (second.total_gastado || 0) - (first.total_gastado || 0));
}

export function opsTeamAgents(clients) {
  const teams = new Map();
  clients.forEach(client => client.productos.forEach(product => product.oportunidades.forEach(op => op.equipos.forEach(team => {
    const group = teams.get(team.team_id) || { team_id: team.team_id, nombre: team.nombre, rows: [], agents: new Map(), restricted: false };
    group.rows.push(team);
    if (team.puede_ver_detalle !== true) group.restricted = true;
    else team.perfiles.forEach(profile => (profile.registros || []).forEach(record => {
      const key = JSON.stringify([record.agent_id || record.nombre_colaborador, profile.nombre]);
      const agent = group.agents.get(key) || { nombre: record.nombre_colaborador || 'Sin agente', perfil: profile.nombre,
        costo_operativo: 0, costo_administrativo: 0, total_gastado: 0, horas_operativas: 0, horas_administrativas: 0 };
      const operational = record.categoria === 'Operativo';
      const field = operational ? 'costo_operativo' : 'costo_administrativo';
      agent[field] = opsSum([agent[field], record.costo]);
      agent[operational ? 'horas_operativas' : 'horas_administrativas'] += Number(record.horas || 0);
      agent.total_gastado = opsSum([agent.costo_operativo, agent.costo_administrativo]);
      group.agents.set(key, agent);
    }));
    teams.set(team.team_id, group);
  }))));
  return [...teams.values()].map(team => ({ ...opsAggregate(team.rows), team_id: team.team_id, nombre: team.nombre,
    horas: opsSum(team.rows.map(row => Number(row.horas_operativas || 0) + Number(row.horas_administrativas || 0))),
    restricted: team.restricted, agents: [...team.agents.values()].sort((first, second) => (second.total_gastado || 0) - (first.total_gastado || 0))
  })).sort((first, second) => (second.total_gastado || 0) - (first.total_gastado || 0));
}